import { DISPLAY_CURRENCY_ORIGINAL } from '@affdice/rules/display-currency';

/*
 * §21.7 — re-exported rather than restated, so a console can offer the sentinel
 * `api-core` accepts. One list, owned by `@affdice/rules`, for the reason
 * `i18n.ts` gives about the shipped languages: a picker and an API that
 * disagree about what a value means is how a saved choice becomes a different
 * one on the way back.
 */
export { DISPLAY_CURRENCY_ORIGINAL };

/**
 * Base currencies the platform supports for operator and brand accounting.
 *
 * Matches `ref_currencies` fiat rows (migration 0050). Base currency is chosen
 * once at onboarding and cannot be free-typed — wrong codes fail late and look
 * plausible until an invoice is wrong.
 */

export const BASE_CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — Pound Sterling' },
] as const;

export type BaseCurrency = (typeof BASE_CURRENCIES)[number]['value'];

export const DEFAULT_BASE_CURRENCY: BaseCurrency = 'USD';

export function baseCurrencyOptions(): readonly { value: BaseCurrency; label: string }[] {
  return BASE_CURRENCIES;
}

/* ------------------------------------------------------------------ *
 * §1.3d / §21.7 — how many minor-unit digits a currency has
 * ------------------------------------------------------------------ */

/**
 * What a currency this tab has not been told about is formatted as.
 *
 * Two, and not zero, because of which way the mistake falls. Every fiat row in
 * `ref_currencies` has a scale of two apart from the handful that have none, so
 * two is right far more often than it is wrong; and the case where the map is
 * empty is not "one exotic currency arrived" but "the currency list did not
 * load at all", where every figure on the screen is a two-digit one. Falling
 * back to zero there would render every amount on every console a hundred times
 * too large. Falling back to two leaves a zero-decimal currency looking a
 * hundred times too small in the same situation — worse than right, but it is
 * also exactly what this platform rendered before the map existed, so a failed
 * fetch is a state the consoles have always survived rather than a new one.
 */
export const DEFAULT_MINOR_UNIT_SCALE = 2;

/**
 * Currency → minor-unit digits, for this browser tab.
 *
 * Module state, for the reason `setDisplayLocale` is (see `i18n.ts`): this
 * package runs in three browser consoles and in nothing on the server, so the
 * process it lives in has one reader looking at one screen. The alternative was
 * threading an exponent through some eighty-four `formatMoney` call sites to
 * express a fact none of them knows — and the evidence that this does not work
 * is that `formatMoney`'s `exponent` parameter has existed all along and not
 * one call site ever passed it. `ref_currencies.minor_unit_scale` is real, is
 * editable from the AMS console, and reached `packages/money` on the server
 * only, so a reader looking at JPY was shown a figure a hundred times too
 * large.
 *
 * The server-composed case keeps its explicit parameter: `formatMoney(m, 0)`
 * still wins over this map, the same way passing a locale still wins over the
 * display locale.
 */
const scales = new Map<string, number>();

/**
 * Tell this tab what the platform's currencies are, from `ref_currencies`.
 *
 * Called once at boot beside `setDisplayLocale`, from the currency list each
 * console can reach (`GET /v1/admin/currencies`, `GET /v1/currencies`). Rows
 * with a scale that is not a whole number of digits are ignored rather than
 * stored: a bad row should leave a figure formatted as it was, not as NaN.
 *
 * Replaces what was there rather than merging, so a currency deactivated in the
 * AMS console stops being known here on the next boot, and so a test can reset
 * with `setCurrencyScales([])`.
 */
export function setCurrencyScales(
  rows: readonly { code: string; minor_unit_scale?: number | null }[],
): void {
  scales.clear();
  for (const row of rows) {
    const scale = row.minor_unit_scale;
    if (typeof scale !== 'number' || !Number.isInteger(scale) || scale < 0 || scale > 8) continue;
    scales.set(row.code, scale);
  }
}

/** How many digits after the separator this currency is written with (§1.3d). */
export function currencyExponent(code: string): number {
  return scales.get(code) ?? DEFAULT_MINOR_UNIT_SCALE;
}

/* ------------------------------------------------------------------ *
 * §21.7 — the currency this tab reads reports in
 * ------------------------------------------------------------------ */

/**
 * Tab state, for the reason `setDisplayLocale` is (see `i18n.ts`): this
 * package runs in three browser consoles and in nothing on the server, so the
 * process it lives in has one reader with one preference. The alternative was
 * threading two fields from `me()` down through the statement, invoice,
 * payment and ageing screens to express one fact about the person looking at
 * them, and the argument against that is `formatMoney`'s `exponent`, which
 * existed for the same purpose and was never once passed.
 *
 * Both halves are kept, not the resolved answer: "chose ORIGINAL" and "never
 * chose, and the operator's default is ORIGINAL" produce the same figures and
 * are not the same sentence, which is the distinction E37 recorded for the
 * language.
 */
let chosenCurrency: string | null = null;
let defaultCurrency: string = DISPLAY_CURRENCY_ORIGINAL;

/**
 * What this tab's reader chose, and what applies to them until they do.
 *
 * Called at boot beside `setDisplayLocale`, from `me()`. Omitting
 * `operatorDefault` resets it to `ORIGINAL` — no conversion — rather than
 * leaving the last one standing, so a test can put the tab back where it
 * started with `setDisplayCurrency(null)`.
 */
export function setDisplayCurrency(
  chosen: string | null,
  operatorDefault: string = DISPLAY_CURRENCY_ORIGINAL,
): void {
  chosenCurrency = chosen;
  defaultCurrency = operatorDefault || DISPLAY_CURRENCY_ORIGINAL;
}

/** The pair, unresolved. `chosen` is null when this reader never chose one. */
export function displayCurrency(): { chosen: string | null; operatorDefault: string } {
  return { chosen: chosenCurrency, operatorDefault: defaultCurrency };
}
